import { createWebHistory, createRouter } from 'vue-router'
import HomePage from '@/views/HomePage.vue'
import LoginPage from '@/views/LoginPage.vue'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import CreateUsers from '../views/CreateUsers.vue'
import CreatePassword from '../views/CreatePassword.vue'
import CopyManager from '../views/CopyManager.vue'
import ReportsPage from '../views/ReportsPage.vue'
import SalesPage from '../views/SalesPage.vue'
import MobileMessage from '../views/MobileMessage.vue'
import { handleErrors } from '../shared/utils/errorHandler'
import navigationMonitor from '../shared/services/NavigationMonitorService'
import { isInstanceManagementEnabled } from '../shared/utils/instanceManagementEnvironment'
import { PERMISSION_KEYS } from '../shared/access/permissionKeys'
import { useAuthStore } from '../shared/stores/AuthStore'
import SalesComplianceSolutions from '../views/SalesComplianceSolutions.vue'
import LoadEmployers from '../views/LoadEmployers.vue'
import CheckLoadEmployers from '../views/CheckLoadEmployers.vue'
import ContentLibrary from '../views/ContentLibrary.vue'
import BCSContentLibraryTaxonomyItems from '../views/BCSContentLibraryTaxonomyItems.vue'

const routes = [
  {
    path: '/',
    name: 'sales',
    component: SalesPage,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresAuth: false, desktopOnly: true }
  },
  {
    path: '/home',
    name: 'home',
    component: HomePage,
    meta: { requiresAuth: true }
  },
  {
    path: '/contentLibrary',
    name: 'contentLibrary',
    component: ContentLibrary,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.CONTENT_MANAGER_ACCESS]
    }
  },
  {
    path: '/content-library-items/:taxonomyGroupId',
    name: 'bcsContentLibraryTaxonomyItems',
    component: BCSContentLibraryTaxonomyItems,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS]
    }
  },
  {
    path: '/createUsers',
    name: 'createUsers',
    component: CreateUsers,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/loadEmployers',
    name: 'loadEmployers',
    component: LoadEmployers,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/checkLoadEmployers',
    name: 'checkLoadEmployers',
    component: CheckLoadEmployers,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/reports',
    name: 'reports',
    component: ReportsPage,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/createPassword',
    name: 'createPassword',
    component: CreatePassword,
    meta: {
      desktopOnly: true
    }
  },
  {
    path: '/resetPassword',
    name: 'resetPassword',
    component: CreatePassword,
    meta: {
      desktopOnly: true
    }
  },
  {
    path: '/sales',
    redirect: '/'
  },
  {
    path: '/mobile-message',
    name: 'mobileMessage',
    component: MobileMessage
  },
  {
    path: '/copy-manager',
    name: 'copyManager',
    component: CopyManager,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.CONTENT_MANAGER_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/sales-compliance-solutions',
    name: 'salesComplianceSolutions',
    component: SalesComplianceSolutions,
    meta: {
      iframeOnly: false
    }
  },
  {
    path: '/terms-and-conditions',
    name: 'termsAndConditionsPage',
    component: () => import('../views/TermsAndConditionsPage.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacyPolicyPage',
    component: () => import('../views/PrivacyPolicyPage.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/system-settings',
    name: 'systemSettings',
    component: () => import('../views/SystemSettings.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/agencyAdminTool',
    name: 'agencyAdminTool',
    component: () => import('../views/AgencyAdminTool.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/accessCatalog',
    name: 'accessCatalog',
    component: () => import('../views/AccessCatalogManager.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/transformTracker',
    name: 'transformTracker',
    component: () => import('../views/TransformTracker.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  },
  {
    path: '/kajabi-compare',
    name: 'kajabiCompare',
    component: () => import('../views/KajabiCompare.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  }
]

if (isInstanceManagementEnabled) {
  routes.push({
    path: '/startup',
    name: 'instanceStartup',
    component: () => import('../views/InstanceStartupScreen.vue'),
    meta: { requiresAuth: false }
  })
}

if (import.meta.env.VITE_PROJECT_TYPE_FIREBASE === 'qa') {
  routes.push({
    path: '/dataCleanUp',
    name: 'dataCleanUp',
    component: () => import('../views/DataCleanUp.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  })
  routes.push({
    path: '/agencyCleanUp',
    name: 'agencyCleanUp',
    component: () => import('../views/AgencyCleanUp.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  })
  routes.push({
    path: '/dataGenerator',
    name: 'dataGenerator',
    component: () => import('../views/DataGenerator.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  })
  routes.push({
    path: '/carboneTemplateAudit',
    name: 'carboneTemplateAudit',
    component: () => import('../views/CarboneTemplateAudit.vue'),
    meta: {
      requiresAuth: true,
      permissions: [PERMISSION_KEYS.PLATFORM_ADMIN_ACCESS],
      desktopOnly: true
    }
  })
}

const router = createRouter({
  history: createWebHistory('/'),
  routes
})

const isSmallScreen = () => {
  if (localStorage.getItem('bypassMobileCheck') === 'true') {
    return false
  }
  return window.innerWidth < 1000
}

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

// Plain object (not a Vue ref) — intentional. This is read imperatively by the
// router guard on each navigation, so Vue reactivity is not needed. Using a Vue
// ref here would require importing from 'vue' in the router module.
export const instancesRunning = { value: true }
export function setInstanceStatus(running) {
  instancesRunning.value = running
}

router.beforeEach(async (to, from, next) => {
  const auth = getAuth()
  const user = await new Promise((resolve) => onAuthStateChanged(auth, resolve))

  // Feeds the redirect-loop detector; each forced redirect is also recorded
  // as a Sentry breadcrumb so a bounce between guards can be reconstructed.
  const recordRedirect = (target, reason) => {
    navigationMonitor.recordGuardRedirect({ from: to.fullPath, to: target, reason })
  }

  // Guard against invalid/undefined routes from corrupted history state
  if (!to.path || to.path.includes('undefined') || to.fullPath.includes('undefined')) {
    if (user) {
      recordRedirect('home', 'corrupted-history')
      return next({ name: 'home', replace: true })
    } else {
      recordRedirect('login', 'corrupted-history')
      return next({ name: 'login', replace: true })
    }
  }

  // Instance guard: if instances are stopped, redirect to startup screen
  // Note: on initial load, instancesRunning may not be set yet —
  // App.vue handles the first redirect when the RT DB value arrives.
  if (isInstanceManagementEnabled) {
    if (!instancesRunning.value && to.name !== 'instanceStartup') {
      recordRedirect('instanceStartup', 'instances-stopped')
      return next({ name: 'instanceStartup', replace: true })
    }
    if (instancesRunning.value && to.name === 'instanceStartup') {
      recordRedirect(user ? 'home' : 'login', 'instances-running')
      return next({ name: user ? 'home' : 'login', replace: true })
    }
  }

  // Rest of the router logic
  const isAuthenticated = !!user
  const resetPass = to.query.resetPass === 'true'

  /*
   * Two-factor gate. The Firebase session already exists at this point (the code step
   * lives inside /login), so this runs before the requiresAuth and authenticated-on-public
   * branches below, which would otherwise bounce the user straight to home.
   */
  if (isAuthenticated) {
    const authStore = useAuthStore()
    /* A session with no `bcsSession` claim (opened before the exchange shipped) is upgraded in
     * place here; the exchange may turn out to still owe the code. */
    const mfaPending = await authStore.refreshMfaPending()
      || (await authStore.ensureTrustedSession()).required
    if (mfaPending) {
      if (to.name === 'login') {
        // Already where the code step lives. No recordRedirect here: the loop detector would
        // fire on every legitimate landing.
        return next()
      }
      if (to.meta.requiresAuth) {
        recordRedirect('login', 'mfa-required')
        return next({ name: 'login', query: to.query, replace: true })
      }
      // Public pages still render for an unverified user; only the bounce to home is skipped.
      return next()
    }
  }

  if (to.meta.iframeOnly && !isInIframe()) {
    recordRedirect('sales', 'iframe-only')
    return next({ name: 'sales' })
  }

  if (to.meta.desktopOnly && isSmallScreen()) {
    recordRedirect('mobileMessage', 'desktop-only')
    return next({
      name: 'mobileMessage',
      query: { returnRoute: to.fullPath }
    })
  }

  if (!isAuthenticated && to.path === '/' && to.query.view) {
    // Deep link landing while logged out: `/` is public, so the requiresAuth guard below never
    // fires and the user would be stranded on the landing page with the deep-link query unused.
    // Send them to login (preserving the query) so after authenticating they reach the requested
    // view instead of the public site.
    recordRedirect('login', 'deep-link-logged-out')
    next({ name: 'login', query: to.query, replace: true })
  } else if (to.meta.requiresAuth && !isAuthenticated) {
    // Preserve query params for deep link support
    recordRedirect('login', 'requires-auth')
    next({ name: 'login', query: to.query, replace: true })
  } else if ((to.name === 'login' || to.name === 'resetPassword' || to.path === '/' || to.name === 'termsAndConditionsPage' || to.name === 'privacyPolicyPage') && isAuthenticated && !resetPass) {
    // Preserve query params for deep link support
    recordRedirect('home', 'authenticated-on-public')
    next({ name: 'home', query: to.query, replace: true })
  } else if (to.meta.permissions && isAuthenticated) {
    try {
      const authStore = useAuthStore()
      await authStore.ensureAccessLoaded()
      if (!authStore.hasAnyPermission(to.meta.permissions)) {
        recordRedirect('home', 'missing-permission')
        return next({ name: 'home', replace: true })
      }
    } catch {
      recordRedirect('home', 'access-load-failed')
      return next({ name: 'home', replace: true })
    }
    next()
  } else {
    next()
  }
})

// Breadcrumb trail for real (vue-router) route changes; virtual /home sub-views
// are recorded by NavigationStore. Guard redirects are recorded separately in
// beforeEach, so this only logs the navigations that actually completed.
router.afterEach((to, from) => {
  navigationMonitor.recordNavigation({
    from: from.fullPath ? navigationMonitor.sanitizeUrl(from.fullPath) : '(initial)',
    to: navigationMonitor.sanitizeUrl(to.fullPath),
    action: 'push',
    source: 'router'
  })
})

let resizeTimeout = null;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    if (!router || !router.currentRoute || !router.currentRoute.value) return;
    const currentRoute = router.currentRoute.value;
    if (currentRoute.meta.desktopOnly && isSmallScreen()) {
      router.replace({
        name: 'mobileMessage',
        query: { returnRoute: currentRoute.fullPath }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          handleErrors('router resizeTimeout', err)
        }
      })
    }
    else if (currentRoute.name === 'mobileMessage' && !isSmallScreen() && currentRoute.query.returnRoute) {
      router.replace(currentRoute.query.returnRoute).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          handleErrors('router resizeTimeout', err)
          window.location.href = currentRoute.query.returnRoute
        }
      })
    }
  }, 250)
})

export default router
